<template>
  <div>
    <a-tag
      :color="data.length == 200 ? 'red' : 'green'"
      style="position: absolute; right: 40px; font-size: 15px"
    >
      {{ data.length }}  {{ $t('appreciation.appreciation') }}
    </a-tag>

    <a-button
      icon="book"
      :disabled="data.length >= 200 || isReadOnly"
      @click="showModal"
      style="margin-bottom: 20px"
      :loading="loading"
    >
      {{ $t('appreciation.add') }}
    </a-button>

    <a-modal
      v-model="visibleModal"
      @cancel="handleCancel"
      :title="$t('all.enterInformationBelow')"
      :footer="false"
    >
      <a-form :form="form" @submit="addAppreciation">
        <a-form-item :label="$t('appreciation.appreciation')">
          <a-textarea
            :maxLength="50"
            v-decorator="[
              'appreciation',
              {
                rules: [
                  {
                    required: true,
                    message: $t('requis.appreciation'),
                  },
                ],
              },
            ]"
            :placeholder="$t('appreciation.appreciation')"
            :auto-size="{ minRows: 2, maxRows: 5 }"
          />
        </a-form-item>
        <div class="form-actions mt-0">
          <a-button
            type="primary"
            htmlType="submit"
            :loading="loading"
            :disabled="loading || isReadOnly"
            class="mr-3"
          >
            {{ $t("action.ajouter") }}
          </a-button>
          <a-button
            key="back"
            @click="handleCancel"
            :disabled="loading"
          >{{ $t("action.annuler") }}</a-button>
        </div>
      </a-form>
    </a-modal>

    <div class="card">
      <a-table
        :rowKey="'_id'"
        :columns="columns"
        :data-source="data"
        :pagination="{ pageSize: 5 }"
        :loading="tableLoading"
      >
        <template slot="appreciation" slot-scope="text, record">
          <div :key="'appreciation' + record._id">
            <a-textarea
              v-if="record.editable"
              style="margin: -5px 0"
              :value="text"
              @change="(e) => handleChange(e.target.value, record._id, 'appreciation')"
            />
            <template v-else>{{ text }}</template>
          </div>
        </template>
        <span slot="operation" slot-scope="text, record">
          <div class="editable-row-operations">
            <span v-if="record.editable">
              <a-button
                size="small"
                style="margin-left: 5px; margin-right: 5px"
                type="primary"
                @click="() => save(record._id)"
                :loading="savingId === record._id"
                :disabled="isReadOnly"
              >
                {{ $t("action.enregistrer") }}
              </a-button>
              <a-button size="small" type="danger" :disabled="savingId || isReadOnly">
                <a-popconfirm
                  :title="$t('all.sureToCancelAlert')"
                  @confirm="() => cancel(record._id)"
                >
                  <a>{{ $t("action.annuler") }}</a>
                </a-popconfirm>
              </a-button>
            </span>
            <span v-else>
              <a-button
                type="primary"
                :disabled="editingKey !== '' || isReadOnly"
                @click="() => edit(record._id)"
              >
                <a-icon type="edit" />{{ $t("action.modifier") }}
              </a-button>
            </span>
          </div>
        </span>
        <span slot="operationSupprimer" slot-scope="text, record">
          <div class="editable-row-operations">
            <span>
              <a-button :disabled="editingKey !== '' || isReadOnly" type="danger">
                <a-popconfirm
                  :title="$t('requis.supp')"
                  @confirm="() => deleteAppreciation(record._id)"
                >
                  <a>{{ $t("action.supprimer") }}</a>
                </a-popconfirm>
              </a-button>
            </span>
          </div>
        </span>
      </a-table>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { mapState, mapGetters } from "vuex";

export default {
  name: 'AdminAppreciation',
  beforeMount() {
    this.form = this.$form.createForm(this, { name: 'AdminAppreciationForm' });
  },
  computed: {
    ...mapState(["settings", "user"]),
    ...mapGetters("settings", ["packDetailsGetter"]),
    isReadOnly() {
      const value = this.packDetailsGetter?.access?.edManager?.adminAppreciation;
      return value === "ro";
    },
    columns() {
      return [
        {
          title: this.$t('appreciation.appreciation'),
          dataIndex: 'appreciation',
          key: 'appreciation',
          scopedSlots: { customRender: 'appreciation' },
        },
        {
          title: this.$t('action.modifier'),
          dataIndex: 'operation',
          scopedSlots: { customRender: 'operation' },
          fixed: 'right',
        },
        {
          title: this.$t('action.supprimer'),
          dataIndex: 'operationSupprimer',
          scopedSlots: { customRender: 'operationSupprimer' },
          fixed: 'right',
        },
      ];
    },
  },
  data() {
    return {
      data: [],
      cacheData: [],
      loading: false,
      tableLoading: false,
      savingId: null,
      visibleModal: false,
      editingKey: '',
      form: this.$form.createForm(this),
    };
  },
  created() {
    console.log("[AdminAppreciation] Component Created - Full Pack:", this.packDetailsGetter);
    this.fetchData();
  },
  methods: {
    handleApiError(error, defaultMessage) {
      // Check for PACK_PERMISSION_ERROR
      if (
        error.response?.data?.errorCode === "PACK_PERMISSION_ERROR" ||
        error.response?.data?.error?.errorCode === "PACK_PERMISSION_ERROR"
      ) {
        this.$message.error(this.$t("errorCode.PACK_PERMISSION_ERROR"));
        return;
      }
      // Fallback to default error message
      this.$message.error(
        error.response?.data?.message || defaultMessage || this.$t("error.erreur")
      );
    },
    async fetchData() {
      this.tableLoading = true;
      try {
        const response = await apiClient.post('/bulletin/v2/static-admin-appreciation/filter', {});
        this.data = response.data.map(item => ({
          ...item,
          key: item._id
        }));
        this.cacheData = this.data.map(item => ({ ...item }));
      } catch (error) {
        console.error('Error fetching data:', error);
        this.$message.error(this.$t('error.fetchError'));
      } finally {
        this.tableLoading = false;
      }
    },
    showModal() {
      this.visibleModal = true;
    },
    handleCancel() {
      this.form.resetFields();
      this.visibleModal = false;
    },
    async addAppreciation(e) {
      e.preventDefault();
      this.form.validateFieldsAndScroll(async (err, values) => {
        if (!err) {
          this.loading = true;
          try {
            await apiClient.put('/bulletin/v2/static-admin-appreciation', {
              modelName: 'StaticAdminAppreciation',
              query: { appreciation: values.appreciation }
            });
            this.$message.success(this.$t('success.ajout'));
            this.handleCancel();
            await this.fetchData();
          } catch (error) {
            console.error('Error adding appreciation:', error);
            this.handleApiError(error, this.$t('error.ajout'));
          } finally {
            this.loading = false;
          }
        }
      });
    },
    handleChange(value, id, column) {
      const newData = [...this.data];
      const target = newData.find(item => id === item._id);
      if (target) {
        target[column] = value;
        this.data = newData;
      }
    },
    edit(id) {
      const newData = [...this.data];
      const target = newData.find(item => id === item._id);
      this.editingKey = id;
      if (target) {
        target.editable = true;
        this.data = newData;
      }
    },
    async save(id) {
      const newData = [...this.data];
      const target = newData.find(item => id === item._id);

      if (target) {
        this.savingId = id;
        try {
          await apiClient.patch(`/bulletin/v2/static-admin-appreciation/${id}`, {
            modelName: 'StaticAdminAppreciation',
            query: { appreciation: target.appreciation }
          });

          delete target.editable;
          this.data = newData;
          this.editingKey = '';
          this.$message.success(this.$t('success.modification'));
        } catch (error) {
          console.error('Error updating appreciation:', error);
          this.handleApiError(error, this.$t('error.modification'));
          this.cancel(id);
        } finally {
          this.savingId = null;
        }
      }
    },
    cancel(id) {
      const newData = [...this.data];
      const target = newData.find(item => id === item._id);
      this.editingKey = '';
      if (target) {
        Object.assign(
          target,
          this.cacheData.find(item => id === item._id)
        );
        delete target.editable;
        this.data = newData;
      }
    },
    async deleteAppreciation(id) {
      try {
        await apiClient.delete(`bulletin/v2/static-admin-appreciation/${id}`, {
          data: { modelName: 'StaticAdminAppreciation' }
        });
        this.data = this.data.filter(item => item._id !== id);
        this.cacheData = this.cacheData.filter(item => item._id !== id);
        this.$message.success(this.$t('success.suppression'));
      } catch (error) {
        console.error('Error deleting appreciation:', error);
        this.handleApiError(error, this.$t('error.suppression'));
      }
    },
  },
};
</script>

<style scoped>
.card {
  padding: 20px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.form-actions {
  text-align: right;
  margin-top: 24px;
}

.mt-0 {
  margin-top: 0;
}

.mr-3 {
  margin-right: 12px;
}

.editable-row-operations {
  display: flex;
  justify-content: center;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
